import { useRef, useEffect } from "react";
import type { VoiceModePhase } from "./useVoiceMode";
import { behaviorForPhase } from "./voiceOrbBehavior";
import {
  COLORS_NORMAL,
  COLORS_PROCESSING,
  clamp,
  lerp,
  paint,
  setColors,
} from "./voiceOrbRender";

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface VoiceOrbProps {
  phase: VoiceModePhase;
  size?: number;
  getOutputVolume?: () => number;
  /** TTS volume is ducked because user started speaking (barge-in preview) */
  ducking?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function VoiceOrb({
  phase,
  size = 200,
  getOutputVolume,
  ducking = false,
}: VoiceOrbProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Live props accessible from the rAF loop without re-mounting
  const propsRef = useRef({ phase, getOutputVolume, ducking });
  propsRef.current = { phase, getOutputVolume, ducking };

  // Persistent animation runtime state (survives across frames)
  const runtimeRef = useRef({
    time: 0,
    last: 0,
    energy: 0.10,
    smoothScale: 1.0,
    smoothOpacity: 1.0,
    width: 0,
    height: 0,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let frameId = 0;
    let resizeObserver: ResizeObserver | undefined;

    const canvasSize = size + 80; // extra room for aura/glow bleed

    const resize = () => {
      const w = canvasSize;
      const h = canvasSize;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      runtimeRef.current.width = w;
      runtimeRef.current.height = h;
    };

    const tick = (now: number): void => {
      const runtime = runtimeRef.current;
      const liveProps = propsRef.current;
      const behavior = behaviorForPhase(liveProps.phase);

      if (!runtime.last) {
        runtime.last = now;
      }

      const delta = Math.min((now - runtime.last) / 1000, 0.033);
      runtime.last = now;

      // Advance time scaled by state speed --
      // idle crawls (0.74x), speaking races (1.18x)
      runtime.time += delta * behavior.speed;

      // ----- Energy from state + audio -----
      // Audio is only meaningful during "speaking" (getOutputVolume
      // returns 0 for all other phases per useVoiceMode contract).
      const rawAudio =
        liveProps.phase === "speaking" && liveProps.getOutputVolume
          ? liveProps.getOutputVolume()
          : null;
      const inputLevel =
        rawAudio !== null ? clamp(rawAudio, 0, 1) : null;

      // Compute target energy using the behavior profile:
      // - With audio: fromAudio maps volume directly to energy
      // - Without audio: fallback provides organic self-animation
      const rawTarget =
        inputLevel === null
          ? behavior.fallback(runtime.time)
          : behavior.fromAudio(inputLevel, runtime.time);

      const target = clamp(rawTarget, 0.07, 0.85);

      // Smoothing: use behavior's blend when audio-driven (snappy response),
      // cap at 0.08 for non-audio states (prevents jarring jumps on phase change)
      const blend =
        inputLevel === null
          ? Math.min(behavior.smoothing, 0.08)
          : behavior.smoothing;
      runtime.energy = lerp(runtime.energy, target, blend);

      // ----- Ring scale (breathing / contraction per phase) -----
      const targetScale = behavior.scale(runtime.time);
      runtime.smoothScale = lerp(runtime.smoothScale, targetScale, 0.08);

      // Scale the virtual paint dimensions so the ring radius
      // (derived from min(w,h) * 0.285) grows or shrinks while
      // the actual canvas buffer stays fixed.
      const scaledW = runtime.width * runtime.smoothScale;
      const scaledH = runtime.height * runtime.smoothScale;

      // Opacity — processing state fades; ducking dims to show TTS is lowered
      const targetOpacity = liveProps.ducking ? 0.45 : behavior.opacity;
      runtime.smoothOpacity = lerp(runtime.smoothOpacity, targetOpacity, 0.12);

      // Color shift — processing uses deeper, muted blue
      setColors(liveProps.phase === "processing" ? COLORS_PROCESSING : COLORS_NORMAL);

      // Clear full canvas, then offset so the scaled ring stays centered
      try {
        ctx.clearRect(0, 0, runtime.width, runtime.height);
        ctx.save();
        ctx.globalAlpha = runtime.smoothOpacity;
        const ox = (runtime.width - scaledW) / 2;
        const oy = (runtime.height - scaledH) / 2;
        ctx.translate(ox, oy);
        paint(ctx, scaledW, scaledH, runtime.time, runtime.energy);
        ctx.restore();
      } catch (err) {
        console.warn("[VoiceOrb] Canvas render error:", err);
      }

      frameId = window.requestAnimationFrame(tick);
    };

    resize();

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(canvas);
    }

    frameId = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(frameId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [size]);

  return (
    <canvas
      ref={canvasRef}
      className="voice-orb-canvas"
      role="status"
      aria-label={`Voice assistant ${phase}`}
    />
  );
}
